import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FiUser, FiMenu, FiBell, FiX, FiBriefcase, FiSettings, FiLogOut, FiAward, FiStar, FiZap, FiRefreshCw, FiEdit3, FiMessageCircle, FiChevronRight, FiMail, FiMoreVertical, FiSearch } from 'react-icons/fi';
import { CiCalculator2 } from 'react-icons/ci';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useClient } from '../contexts/ClientContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { useHeaderBootstrap } from '../hooks/useHeaderBootstrap';
import { useNotificationsCount, useOptimisticNotificationsCount, useEmailsCount } from '../hooks/useHeaderCounts';
import { hasFullPermissions, isAdminRole } from '../config/permissionsConfig';
import SwitchAccount from './SwitchAccount';
import { getNotifications, markNotificationRead, deleteNotification } from '../api/notificationsApi';
import { getCompanyFeatures, getTenantLogo, logout } from '../api/authApi';
import { apiClient } from '../api/apiClient';
import { useEmailStore } from '../stores/emailStore';
import EmailIcon from './EmailIcon';
import EmailInboxPopup from './EmailInboxPopup';
import { normalizeCompanySlug } from '../utils/company';
import { queryClient } from '../lib/queryClient';
import { useSocketSubscription, useSocketConnection } from '../lib/socketManager';
import { logoutSuperadminToLogin } from '../utils/superadminAuth';
import { createForceLogout } from '../utils/forceLogout';
import { getSocketUrl } from '../utils/socketUrl';
import { LogoutOverlay } from './auth/LogoutOverlay';
import { useLang } from '../contexts/LanguageContext';
import GlobalSearch from './header/GlobalSearch';
import { clientKeys, leadKeys } from '../lib/queryKeys';
const baseUrl = getSocketUrl();

interface Notification {
  id: string;
  text: string;
  createdAt: string;
  read: boolean;
  type?: string; // 'task_reminder' | 'new_task' | 'task_note' | ...
  refId?: string; // Reference ID (e.g., taskId for task_note notifications)
}

/** Get first path segment as tenant, ignoring reserved app prefixes */
function tenantFromPathname(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] || '';
  if (['admin', 'manager', 'agent', 'app'].includes(seg)) return '';
  return seg;
}

/** Cloudinary thumbnail helper */
function cldThumb(url: string, w = 24, h = 24) {
  try {
    const i = url.indexOf('/upload/');
    if (i === -1) return url;
    return `${url.slice(0, i + 8)}c_fill,q_auto,f_auto,w_${w},h_${h}/${url.slice(i + 8)}`;
  } catch {
    return url;
  }
}

/** Initials helper */
function initialsOf(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() || '')
    .join('');
}

/** Hook: always derive tenant from the current URL */
function useTenantFromPath() {
  const { pathname } = useLocation();
  return useMemo(() => tenantFromPathname(pathname), [pathname]);
}

const Header: React.FC<{ onToggleSidebar?: () => void }> = ({ onToggleSidebar }) => {
  const { t } = useLang();
  const { client: clientFromContext } = useClient(); // Keep for fallback
  const navigate = useNavigate();
  const location = useLocation();
  
  /** 🚀 FAST HEADER: Use header bootstrap query (cached, non-blocking) */
  const { company: companyFromParams } = useParams<{ company?: string }>();
  const tenant = useTenantFromPath();
  const effectiveTenant = useMemo(() => {
    // Normalize all possible sources of company slug
    const normalizedTenant = normalizeCompanySlug(tenant);
    const normalizedParams = normalizeCompanySlug(companyFromParams);
    const normalizedContext = normalizeCompanySlug(clientFromContext?.company);
    return normalizedTenant || normalizedParams || normalizedContext || '';
  }, [tenant, companyFromParams, clientFromContext?.company || '']);
  
  const { data: bootstrapData, isLoading: bootstrapLoading } = useHeaderBootstrap(
    effectiveTenant || undefined
  );
  
  // Use bootstrap data if available, fallback to context
  const client = bootstrapData?.user || clientFromContext;
  const userPermissions = Array.isArray(client?.permissions) ? (client?.permissions || []) : [];
  const isFullAccessManager = client?.role === 'manager' && hasFullPermissions(userPermissions);
  
  // Fetch fullName from settings - ALWAYS fetch to get latest שם מלא from settings
  const [settingsFullName, setSettingsFullName] = useState<string | null>(null);
  const [isLoadingSettingsName, setIsLoadingSettingsName] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  useEffect(() => {
    const fetchFullNameFromSettings = async () => {
      // Always fetch from settings to get the latest fullName (שם מלא)
      if (!effectiveTenant || !client?._id || !client?.role) return;
      
      setIsLoadingSettingsName(true);
      
      // CRITICAL: Check if user just logged in - if so, delay this request to avoid timeout
      // This prevents blocking the PostLoginLoader
      const justLoggedIn = sessionStorage.getItem('justLoggedIn');
      const justLoggedInTime = sessionStorage.getItem('justLoggedInTime');
      const isRecentLogin = justLoggedIn === 'true' && justLoggedInTime && 
        (Date.now() - parseInt(justLoggedInTime, 10)) < 10000; // Within last 10 seconds
      
      // If user just logged in, delay this request to allow PostLoginLoader to show
      const delay = isRecentLogin ? 1000 : 0;
      
      try {
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Try to get fullName from settings based on user role
        let meData: any = null;
        if (client.role === 'admin') {
          const { getAdminMe } = await import('../api/settingsApi');
          meData = await getAdminMe(effectiveTenant);
        } else if (client.role === 'manager') {
          const { getManagerMe } = await import('../api/settingsApi');
          meData = await getManagerMe(effectiveTenant);
        } else if (client.role === 'client') {
          const { getClientMe } = await import('../api/clientsApi');
          const clientMeData = await getClientMe(effectiveTenant);
          // getClientMe returns { client: { fullName, ... }, bookings: [...] }
          meData = clientMeData?.client || clientMeData;
        }
        
        // Set fullName even if it's empty string - we want to know if it exists
        if (meData?.fullName) {
          setSettingsFullName(meData.fullName);
        }
      } catch (err: any) {
        // Suppress timeout errors - they're expected after login when server is busy
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        // Silently handle errors
      } finally {
        setIsLoadingSettingsName(false);
      }
    };
    
    // Always fetch - settings has the most up-to-date fullName (שם מלא)
    fetchFullNameFromSettings();
  }, [client?._id, client?.role, effectiveTenant]);
  
  // Use settings fullName first (most up-to-date), then bootstrap, then email, then default
  // Only use 'משתמש' if we've finished loading and still don't have a name
  const displayName = settingsFullName || client?.fullName || client?.email || (isLoadingSettingsName ? '' : 'משתמש');
  
  // Get notifications count (lightweight, polling)
  const { data: notificationsCountData } = useNotificationsCount(
    effectiveTenant || undefined,
    client?._id // Pass userId to prevent early API calls
  );
  const unreadNotificationsCount = notificationsCountData?.count || 0;
  const setOptimisticNotificationsCount = useOptimisticNotificationsCount();
  
  // Get emails count (lightweight, polling) - sync with emailStore
  const { data: emailsCountData } = useEmailsCount(effectiveTenant || undefined);
  const unreadEmailsCount = emailsCountData?.count || 0;
  const { setUnreadCount: setEmailStoreCount } = useEmailStore();
  
  // Sync React Query email count with Zustand store (for EmailIcon component)
  useEffect(() => {
    if (unreadEmailsCount !== undefined) {
      setEmailStoreCount(unreadEmailsCount);
    }
  }, [unreadEmailsCount, setEmailStoreCount]);
  
  const [sidebarWidth, setSidebarWidth] = useState<'16rem' | '4.5rem'>(() => {
    if (typeof window === 'undefined') return '16rem';
    return localStorage.getItem('sidebar:collapsed') === '1' ? '4.5rem' : '16rem';
  });

  // Listen for sidebar width changes
  useEffect(() => {
    const updateWidth = () => {
      const collapsed = localStorage.getItem('sidebar:collapsed') === '1';
      setSidebarWidth(collapsed ? '4.5rem' : '16rem');
    };
    window.addEventListener('sidebar:width', updateWidth);
    window.addEventListener('focus', updateWidth);
    return () => {
      window.removeEventListener('sidebar:width', updateWidth);
      window.removeEventListener('focus', updateWidth);
    };
  }, []);
  // ============================================================================
  // CRITICAL: Badge/Plan must ALWAYS come from live context (same as Sidebar)
  // ============================================================================
  // - Do NOT use bootstrap data for badge calculation - it can be stale
  // - Bootstrap is only for initial render speed, but context is the source of truth
  // - Do NOT store badge/plan in local state without watching context changes
  // - Any permission checks MUST read from this live context, not cached values
  // - Future changes: Always respect that badge/plan/permissions are live, not cached
  // ============================================================================
  const { enabledFeatures, hiddenFromSidebar, plan, loading: featuresLoading } = useCompanyFeatures();

  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [modalNotif, setModalNotif] = useState<Notification | null>(null);
  const [adminCompanyName, setAdminCompanyName] = useState<string>('');
  const [busyClear, setBusyClear] = useState(false);
  const [adminLogoUrl, setAdminLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState(false); // Unused but kept for reset effect
  const [adminLogoError, setAdminLogoError] = useState(false); // Track admin logo load errors
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companyBadge, setCompanyBadge] = useState<BadgeType>(null);
  const [switchAccountOpen, setSwitchAccountOpen] = useState(false);
  const [isWideMobile, setIsWideMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  
  // Listen for open-email event from Home page or Sidebar
  useEffect(() => {
    const handleEmailOpen = () => {
      setEmailPopupOpen(true);
    };
    
    window.addEventListener('open-email', handleEmailOpen);
    return () => window.removeEventListener('open-email', handleEmailOpen);
  }, []);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { refreshUnreadCount } = useEmailStore();
  const isSocketConnected = useSocketConnection();

  // Use clientFromContext for fields not in bootstrap (company, endDate)
  const clientForEmail = clientFromContext; // Use context client for email refresh (has endDate)
  const userInitials = useMemo(() => initialsOf(displayName || ''), [displayName]);

  // Detect screen size for responsive layout
  useEffect(() => {
    const checkWidth = () => {
      const width = window.innerWidth;
      setIsWideMobile(width > 375);
      setIsMobile(width < 640);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [userMenuOpen]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };

    if (moreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [moreMenuOpen]);

  // Close search popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [searchOpen]);

  // Check if feature is enabled and visible (matches Sidebar logic)
  const isFeatureEnabled = (feature: string, roles: string[]): boolean => {
    const role = client?.role;
    if (!role || !roles.includes(role)) return false;
    
    // Check if hidden from sidebar (explicit hide rule)
    if (hiddenFromSidebar.includes(feature)) return false;
    
    // If enabledFeatures is null, all features are enabled (superadmin or not loaded yet)
    if (enabledFeatures === null) return true;
    
    // If enabledFeatures is an empty array, no features are enabled
    if (enabledFeatures.length === 0) {
      return false;
    }
    
    // Feature is enabled if it's in the enabledFeatures array
    return enabledFeatures.includes(feature);
  };

  // Utility buttons visibility
  // CRITICAL: Explicitly exclude 'client' role - these features are staff-only
  const isStaff = client?.role && ['admin', 'manager', 'agent', 'superadmin'].includes(client.role);
  const isClient = client?.role === 'client';
  
  // Chat is only available for staff (admin, manager, agent) - not for clients
  const showChat = isStaff && isFeatureEnabled('chat', ['admin', 'manager', 'agent']);
  
  // Show sticky notes and calculator for staff only (exclude clients)
  const showStickyNotes = isStaff && !isClient && isFeatureEnabled('sticky-notes', ['admin', 'manager', 'agent']);
  const showCalculator = isStaff && !isClient && isFeatureEnabled('calculator', ['admin', 'manager', 'agent']);
  
  // Show email icon for staff only (exclude clients)
  const showEmail = isStaff && !isClient;

  // Handle utility button clicks
  const handleChatClick = () => {
    window.dispatchEvent(new CustomEvent('open-chat'));
  };

  const handleStickyNotesClick = () => {
    window.dispatchEvent(new CustomEvent('sticky-notes', { detail: { action: 'toggle' } }));
  };

  const handleCalculatorClick = () => {
    window.dispatchEvent(new CustomEvent('calculator', { detail: { action: 'toggle' } }));
  };

  // Request notification permission (user-triggered only - prevents Chrome violation)
  const requestNotificationPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    
    // Only request if permission is still "default" (not granted/denied)
    if (Notification.permission !== 'default') return;
    
    try {
      await Notification.requestPermission();
    } catch (err) {
      // Silently handle errors (user may have blocked notifications)
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Header] Notification permission request failed:', err);
      }
    }
  }, []);

  /**
   * Logo loading logic:
   * - If bootstrap has logoUrl (string) → use it directly, no API call needed
   * - If bootstrap has logoUrl: null → company has no logo, use default placeholder, no API call
   * - If bootstrap doesn't have logoUrl (undefined) → only then fetch from API
   * - If API returns 404/500 → use default placeholder, no retries
   * - All img elements have onError handlers as final fallback
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Reset error state when company changes
      setAdminLogoError(false);
      
      // If bootstrap explicitly has logoUrl (even if null), use it - no API call needed
      if ('logoUrl' in (bootstrapData?.company || {})) {
        if (!cancelled) {
          setAdminLogoUrl(bootstrapData?.company?.logoUrl || '/crmLogo.png'); // Use default if null
          setLogoLoading(false);
        }
        return;
      }
      
      // Only fetch from API if bootstrap doesn't have logoUrl at all (undefined)
      // Use effective tenant (from path, params, or client)
      const companySlug = effectiveTenant;
      
      // Skip logo fetch for superadmin or invalid slugs
      if (!companySlug || !baseUrl || companySlug === 'superadmin') {
        if (!cancelled) {
          setAdminLogoUrl('/crmLogo.png'); // Use default logo
          setLogoLoading(false);
        }
        return;
      }
      
      // Validate company slug format (basic check to prevent invalid requests)
      if (!/^[a-z0-9_-]+$/i.test(companySlug)) {
        if (!cancelled) {
          setAdminLogoUrl('/crmLogo.png'); // Use default logo
          setLogoLoading(false);
        }
        return;
      }
      
      setLogoLoading(true);
      try {
        // getTenantLogo returns default /crmLogo.png on 404 or any error (no throw, no console spam)
        const { url } = await getTenantLogo(companySlug);
        if (!cancelled) {
          setAdminLogoUrl(url || '/crmLogo.png');
        }
      } catch (_) {
        if (!cancelled) setAdminLogoUrl('/crmLogo.png');
      } finally {
        if (!cancelled) setLogoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveTenant, baseUrl, bootstrapData?.company?.logoUrl]); // Only refetch when company changes, not on every page navigation

  // Reset logo error state when company changes
  useEffect(() => {
    setLogoError(false);
    setAdminLogoError(false);
  }, [effectiveTenant]);

  // Badge logic
  type BadgeType = 'trial' | 'basic' | 'vip' | 'platinum' | 'enterprise' | null;

  const getCompanyBadge = (enabledFeatures: string[] | null = [], companyPlan: string | null = null): BadgeType => {
    // PRIORITY 1: Check plan first - plan-based badges take precedence over features
    // This ensures correct badge is shown even if features haven't been saved yet
    // Normalize plan to lowercase for comparison
    const normalizedPlan = companyPlan ? companyPlan.toLowerCase() : null;
    
    if (normalizedPlan === 'enterprise') {
      return 'enterprise';
    }
    
    // PRIORITY 2: Check for platinum plan
    if (normalizedPlan === 'platinum') {
      return 'platinum';
    }
    
    // PRIORITY 3: Check for VIP plan
    if (normalizedPlan === 'vip') {
      return 'vip';
    }
    
    // PRIORITY 4: Check for basic plan
    if (normalizedPlan === 'basic') {
      return 'basic';
    }
    
    // PRIORITY 5: If plan is trial, always return trial badge regardless of features
    // This ensures new trial accounts show trial badge, not VIP/platinum
    if (normalizedPlan === 'trial' || normalizedPlan === 'trial_plus' || normalizedPlan === 'free') {
      return 'trial';
    }

    if (enabledFeatures === null) {
      // If plan is enterprise, return enterprise; otherwise platinum
      return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
    }
    
    // Basic features
    const basicFeatures = ['dashboard', 'clients', 'managers', 'tasks', 'calculator', 'calendar', 'settings', 'issues', 'about'];
    // VIP features (in addition to basic)
    const vipFeatures = ['agents', 'slots', 'sms'];
    // Trial features (limited subset)
    const trialFeatures = ['dashboard', 'clients', 'managers', 'tasks', 'calendar', 'settings', 'issues', 'about'];
    // All features (must include all features for platinum badge)
    // IMPORTANT: This list must match exactly with the features defined in superadmin-svc
    const allFeatures = [
      'dashboard', 'clients', 'managers', 'agents', 'leads', 'orders', 
      'inventory', 'slots', 'tasks', 'calendar', 'automations', 'sticky-notes', 'calculator', 
      'chat', 'lumira-ai', 'contract-ai', 'sms', 'company-features', 'settings', 'issues', 'about', 'landing-page'
    ];

    // If empty array, treat as all features enabled (backward compatibility)
    if (enabledFeatures.length === 0) {
      return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
    }

    // Check if has all features (platinum/enterprise)
    const hasAllFeatures = allFeatures.every(feature => enabledFeatures.includes(feature));
    if (hasAllFeatures) {
      // Check plan to distinguish between enterprise and platinum
      return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
    }

    // Check if has basic + VIP features
    const hasBasic = basicFeatures.every(feature => enabledFeatures.includes(feature));
    const hasVip = vipFeatures.every(feature => enabledFeatures.includes(feature));
    if (hasBasic && hasVip) {
      return 'vip';
    }

    // Check if has basic features
    if (hasBasic) {
      return 'basic';
    }

    // Check if has trial features only
    const hasTrial = trialFeatures.every(feature => enabledFeatures.includes(feature)) && 
                     enabledFeatures.length === trialFeatures.length;
    if (hasTrial) {
      return 'trial';
    }

    return null;
  };


  // CRITICAL: Badge must be computed directly from context (same as Sidebar)
  // Do NOT store in local state - read directly from context to ensure real-time updates
  // This matches Sidebar's approach exactly for consistency
  useEffect(() => {
    if (!effectiveTenant || effectiveTenant === 'superadmin' || client?.role === 'superadmin') {
      setCompanyBadge('platinum');
      // Persist platinum badge for superadmin
      try {
        localStorage.setItem(`company_badge_${effectiveTenant || 'superadmin'}`, 'platinum');
      } catch (e) {}
      return;
    }

    // Don't show badge until plan is loaded (matches Sidebar behavior exactly)
    // CRITICAL: Use featuresLoading from context, NOT the local loading state
    if (featuresLoading || !plan) {
      setCompanyBadge(null);
      return;
    }

    // Use enabledFeatures from context for real-time badge calculation
    // This directly watches context changes, so it updates immediately when badge changes
    // CRITICAL: Create a new array reference to ensure React detects changes
    const featuresArray = enabledFeatures === null ? null : (enabledFeatures.length > 0 ? [...enabledFeatures] : []);
    const calculatedBadge = getCompanyBadge(featuresArray ?? [], plan);
    
    // Always update badge immediately when features/plan change - don't check prev state
    setCompanyBadge(calculatedBadge);
    // Persist new badge immediately
    try {
      if (calculatedBadge) {
        localStorage.setItem(`company_badge_${effectiveTenant}`, calculatedBadge);
      }
    } catch (e) {}
  }, [effectiveTenant, client?.role, enabledFeatures, plan, featuresLoading]); // CRITICAL: Use featuresLoading from context

  // Note: We don't need a separate event listener here because the main useEffect
  // already watches enabledFeatures and plan from context, which will update automatically
  // when the context receives websocket events. Adding another listener would cause duplicate updates.

  /** Fetch admin company name (prefer local info) */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!effectiveTenant) return;
      // Use bootstrap company name if available
      if (bootstrapData?.company?.name) {
        if (!cancelled) setAdminCompanyName(bootstrapData.company.name);
        return;
      }
      try {
        if (client?.role === 'admin' && clientFromContext?.company) {
          if (!cancelled) setAdminCompanyName(clientFromContext.company);
          return;
        }
        if ((clientFromContext as any)?.admin?.company) {
          if (!cancelled) setAdminCompanyName((clientFromContext as any).admin.company);
          return;
        }
        if ((clientFromContext as any)?.adminCompany) {
          if (!cancelled) setAdminCompanyName((clientFromContext as any).adminCompany);
          return;
        }
        const { data } = await apiClient.get(
          `/api/${effectiveTenant}/admin/company-name`,
          { withCredentials: true }
        );
        if (!cancelled) setAdminCompanyName(data?.companyName || effectiveTenant);
      } catch (e) {
        console.warn('Failed to get admin company; fallback to tenant', e);
        if (!cancelled) setAdminCompanyName(clientFromContext?.company || effectiveTenant);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveTenant, baseUrl, client?.role, bootstrapData?.company?.name, clientFromContext]);

  /** REST helpers (tenant everywhere) */
  const fetchNotifications = useCallback(async () => {
    if (!effectiveTenant) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await getNotifications(effectiveTenant);
      const list = ((res as any)?.notifications || [])
        .slice()
        .sort((a: Notification, b: Notification) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      setNotifications(list);
    } catch (err: any) {
      // Silently handle timeout errors - don't log or show toasts
      const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
      if (!isTimeout) {
        // Only log non-timeout errors
        console.error('⚠️ failed to fetch notifications', err);
      }
    } finally {
      setLoading(false);
    }
  }, [effectiveTenant]);

  // CRITICAL FIX: When opening notifications panel, fetch list immediately
  // This ensures new notifications that arrived via socket are visible
  const handleOpenNotifications = useCallback(() => {
    setOpen(v => {
      const isOpening = !v;
      if (isOpening && effectiveTenant) {
        // CRITICAL: Fetch notifications list BEFORE setting optimistic count
        // This ensures the list is fresh when dropdown opens
        fetchNotifications();
        
        // Then set optimistic count to 0 (for badge)
        setOptimisticNotificationsCount(effectiveTenant);
      }
      return !v;
    });
  }, [effectiveTenant, fetchNotifications, setOptimisticNotificationsCount]);

  const markAsRead = async (notifId: string) => {
    // Get notification to check its type before marking as read
    const notification = notifications.find(n => n.id === notifId);
    const isClientAssignmentNotif = notification?.type === 'assigned_client';
    const isLeadAssignmentNotif = notification?.type === 'lead_assignment';
    
    // Optimistic update: mark as read in local state immediately
    setNotifications(prev => prev.map(n => (n.id === notifId ? { ...n, read: true } : n)));
    
    try {
      await markNotificationRead(effectiveTenant, notifId);
      
      // CRITICAL: Invalidate count query to update badge in real-time
      if (effectiveTenant && client?._id) {
        queryClient.invalidateQueries({
          queryKey: ['header', 'notificationsCount', effectiveTenant, client._id]
        });
      }
      
      // CRITICAL: If this is a client assignment notification and user is a manager,
      // invalidate the manager's clients cache so they see the new client immediately
      if (isClientAssignmentNotif && client?.role === 'manager' && effectiveTenant) {
        queryClient.invalidateQueries({ 
          queryKey: clientKeys.list(effectiveTenant, {}),
          refetchType: 'active' // Force immediate refetch for active queries
        });
      }
      
      // CRITICAL: If this is a lead assignment notification and user is a manager,
      // invalidate the manager's leads cache so they see the new lead immediately
      // Note: Manager leads query uses managerId filter, so we invalidate all leads queries for this manager
      if (isLeadAssignmentNotif && client?.role === 'manager' && effectiveTenant && client?._id) {
        // Invalidate all leads queries for this company (the query will filter by managerId automatically)
        queryClient.invalidateQueries({ 
          queryKey: leadKeys.all(effectiveTenant),
          refetchType: 'active' // Force immediate refetch for active queries
        });
      }
      
      // CRITICAL: Refetch notifications list to ensure it's up to date
      // This ensures new notifications that arrived via socket are included
      fetchNotifications();
    } catch (err) {
      // Revert optimistic update on error
      setNotifications(prev => prev.map(n => (n.id === notifId ? { ...n, read: false } : n)));
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    
    // Check if any unread notification is a client or lead assignment notification
    const hasClientAssignmentNotif = notifications.some(
      n => !n.read && n.type === 'assigned_client'
    );
    const hasLeadAssignmentNotif = notifications.some(
      n => !n.read && n.type === 'lead_assignment'
    );
    
    // Optimistic update: mark all as read in local state immediately
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    
    try {
      await Promise.allSettled(
        unreadIds.map(id => markNotificationRead(effectiveTenant, id))
      );
      
      // CRITICAL: Invalidate count query to update badge in real-time
      if (effectiveTenant && client?._id) {
        queryClient.invalidateQueries({
          queryKey: ['header', 'notificationsCount', effectiveTenant, client._id]
        });
      }
      
      // CRITICAL: If any notification was a client assignment and user is a manager,
      // invalidate the manager's clients cache so they see new clients immediately
      if (hasClientAssignmentNotif && client?.role === 'manager' && effectiveTenant) {
        queryClient.invalidateQueries({ 
          queryKey: clientKeys.list(effectiveTenant, {}),
          refetchType: 'active' // Force immediate refetch for active queries
        });
      }
      
      // CRITICAL: If any notification was a lead assignment and user is a manager,
      // invalidate the manager's leads cache so they see new leads immediately
      if (hasLeadAssignmentNotif && client?.role === 'manager' && effectiveTenant && client?._id) {
        queryClient.invalidateQueries({ 
          queryKey: leadKeys.all(effectiveTenant),
          refetchType: 'active' // Force immediate refetch for active queries
        });
      }
      
      // CRITICAL: Refetch notifications list to ensure it's up to date
      fetchNotifications();
    } catch (err) {
      // Revert optimistic update on error
      setNotifications(prev => prev.map(n => 
        unreadIds.includes(n.id) ? { ...n, read: false } : n
      ));
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  const deleteNotificationHandler = async (notifId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
    try { await deleteNotification(effectiveTenant, notifId); } catch { }
  };

  const clearAllNotifications = async () => {
    if (notifications.length === 0 || busyClear) return;
    setBusyClear(true);
    const ids = notifications.map(n => n.id);
    setNotifications([]); // optimistic
    try {
      await Promise.allSettled(
        ids.map(id => deleteNotification(effectiveTenant, id))
      );
    } catch { }
    setBusyClear(false);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    setConfirmLogout(true);
  };

  const performLogout = async () => {
    setConfirmLogout(false);
    
    // Show logout overlay immediately - MUST be first to ensure it renders
    setIsLoggingOut(true);
    
    // Force a re-render to ensure overlay is painted
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
    
    try {
      // 1) Mark this as manual logout for the interceptors
      const logoutTimestamp = Date.now().toString();
      sessionStorage.setItem('manualLogout', 'true');
      localStorage.setItem('manualLogout', 'true');
      sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
      localStorage.setItem('manualLogoutTime', logoutTimestamp);
      // Also use manualLogoutAt for consistency with interceptor checks
      sessionStorage.setItem('manualLogoutAt', logoutTimestamp);
      localStorage.setItem('manualLogoutAt', logoutTimestamp);
      
      // 2) CRITICAL: Set logoutReason='manual-logout' so Login page knows to perform cleanup
      // The Login page will clear cookies when it sees logoutReason, but won't show error messages for 'manual-logout'
      sessionStorage.setItem('logoutReason', 'manual-logout');
      
      // Clear from URL if present (without reload) - we'll add it back in the redirect URL
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        if (url.searchParams.has('logoutReason')) {
          url.searchParams.delete('logoutReason');
          window.history.replaceState({}, '', url.toString());
        }
      }
      
      // Determine if this is superadmin (from URL or client role)
      const isSuperadmin = location.pathname.startsWith('/superadmin') || client?.role === 'superadmin';
      const companyForLogout = isSuperadmin ? 'superadmin' : (effectiveTenant || '');
            
      if (!companyForLogout) {
        console.error('❌ No company found for logout');
        // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
        logoutSuperadminToLogin('Header::handleLogout_no_company', {
          reason: 'manual_click',
        });
        return;
      }
      
      // 3) Call logout API (if it fails, continue anyway)
      try {
        await logout(companyForLogout, 'manual-button-click');
      } catch (err) {
        console.error('❌ Logout API call failed:', err);
        // Continue with logout even if API call fails
      }
      
      // 4) Clear all React Query cache and CRM-related localStorage on logout
      // to ensure next login starts from a clean slate
      try {
        // 1) Clear all React Query caches (queries + mutations)
        queryClient.clear();        
        // 2) Clear auth snapshot (in-memory auth state)
        try {
          const { clearAuthSnapshot } = await import('../prefetch/api');
          clearAuthSnapshot();
        } catch (err) {
          console.error('[Logout] Error clearing auth snapshot:', err);
        }
        
        // 3) Clear all CRM-related auth and company data from localStorage
        // Keep user preferences like rememberEmail, rememberPassword, device_id
        const oldClient = localStorage.getItem('client');
        if (oldClient) {
          try {
            const parsed = JSON.parse(oldClient);
          } catch (e) {
            console.warn('[Logout] Could not parse client data');
          }
        }
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('client');
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('activeTenantId');
        localStorage.removeItem('activeTenantName');
        localStorage.removeItem('activeRole');
        localStorage.removeItem('lastLogin');
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('wjs_active_company_v2'); // New company slug storage key
        localStorage.removeItem('wjs_superadmin_auth'); // Superadmin-specific auth (if exists)
        
        // 3.5) CRITICAL: Clear cookies that might persist
        // Use centralized auth cookie clear helper so flags (domain, secure, sameSite)
        // match the backend's setAuthCookie/clearAuthCookie behavior.
        if (typeof document !== 'undefined') {
          import('../utils/authCookies').then(({ clearAllAuthCookies }) => {
            clearAllAuthCookies();
          }).catch(err => {
            // Error importing authCookies
          });
        }
        
        // 4) Clear axios default headers
        delete axios.defaults.headers.common.Authorization;
        const apiInstance = (window as any).__apiInstance;
        if (apiInstance) {
          delete apiInstance.defaults.headers.common.Authorization;
        }
      } catch (cleanupErr) {
        console.error('[Logout] Error during cleanup:', cleanupErr);
        // Continue with logout even if cleanup fails
      }
      
      // 5) Wait a moment for the overlay to render, then navigate to login page
      // Use requestAnimationFrame twice to ensure the overlay is painted before redirect
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Small additional delay to ensure overlay is visible
          setTimeout(() => {
            if (isSuperadmin) {
              logoutSuperadminToLogin('Header::handleLogout_superadmin', {
                reason: 'manual_click',
              });
            } else if (effectiveTenant) {
              // For company routes, use forceLogout directly with manual_click
              const forceLogout = createForceLogout(queryClient);
              forceLogout({
                reason: 'manual_click',
                source: 'Header::handleLogout_company',
                companySlug: effectiveTenant,
              });
            } else {
              logoutSuperadminToLogin('Header::handleLogout_fallback', {
                reason: 'manual_click',
              });
            }
          }, 150); // 150ms delay to show the animation
        });
      });
    } catch (redirectErr) {
      console.error('❌ Redirect failed:', redirectErr);
      // Force redirect as last resort - use logoutSuperadminToLogin
      logoutSuperadminToLogin('Header::handleLogout_error_fallback', {
        reason: 'manual_click',
      });
    }
  };

  const handleSettings = () => {
    setUserMenuOpen(false);
    // Navigate to correct settings page based on user role
    if (client?.role === 'admin') {
      navigate(`/${effectiveTenant}/admin/settings`);
    } else if (client?.role === 'manager') {
      navigate(`/${effectiveTenant}/manager/settings`);
    } else if (client?.role === 'agent') {
      navigate(`/${effectiveTenant}/agent/settings`);
    } else if (client?.role === 'client') {
      navigate(`/${effectiveTenant}/client/settings`);
    }
  };

  /** Initial load on tenant change */
  useEffect(() => {
    fetchNotifications();
    refreshUnreadCount(clientForEmail?.endDate, client?.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTenant, baseUrl, client?._id, clientForEmail?.endDate]);

  /** Auto-refresh email unread count every 60 seconds */
  useEffect(() => {
    if (!effectiveTenant || !client?._id) return;
    
    // Don't set up interval if subscription is ending soon
    if (clientForEmail?.endDate) {
      const end = new Date(clientForEmail.endDate);
      const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (diff <= 7) {
        return; // Skip interval setup
      }
    }
    
    const interval = setInterval(() => {
      refreshUnreadCount(clientForEmail?.endDate, client?.role);
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [effectiveTenant, client?._id, clientForEmail?.endDate, refreshUnreadCount, client?.role]);

  /** Socket event handlers for notifications (using centralized socketManager) */
  
  // CRITICAL FIX: Fetch notifications when socket connects OR when tenant changes
  // This ensures we have fresh data when switching companies
  useEffect(() => {
    if (isSocketConnected && client?._id && effectiveTenant) {
      fetchNotifications();
    }
  }, [isSocketConnected, client?._id, effectiveTenant, fetchNotifications]);


  // CRITICAL FIX: Handle new notifications via socketManager subscription
  // This must properly normalize payload, deduplicate, and update both list and count
  const handleNotification = useCallback((payload: any) => {
    // CRITICAL: Normalize payload - backend may send _id or id
    const notificationId = payload.id || payload._id || '';
    if (!notificationId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Header] Received notification without id:', payload);
      }
      return;
    }
    
    // CRITICAL: Ensure createdAt is always present (fallback to now)
    const createdAt = payload.createdAt || new Date().toISOString();
    
    // Normalize to stable Notification interface
    const normalizedPayload: Notification = {
      id: notificationId,
      text: payload.text || '',
      createdAt: createdAt,
      read: payload.read || false,
      type: payload.type,
    };
       
    // CRITICAL: Add/update notification in list immediately (optimistic update)
    // Defer heavy operations to prevent [Violation] handler warnings
    Promise.resolve().then(() => {
      setNotifications(prev => {
        const prevLength = prev.length;
        
        // Check if notification already exists (avoid duplicates)
        const existingIndex = prev.findIndex(n => n.id === normalizedPayload.id);
        
        if (existingIndex >= 0) {
          // Update existing notification (might have been updated on another tab)
          const updated = prev.map((n, idx) => 
            idx === existingIndex ? normalizedPayload : n
          );

          return updated;
        }
        
        // Add new notification at the beginning, then sort by createdAt DESC
        // Optimize: Only sort if list is small, otherwise just prepend (newest first)
        const updated = prev.length < 50 
          ? [normalizedPayload, ...prev].sort((a, b) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
          : [normalizedPayload, ...prev];
        
        
        return updated;
      });
    });
    
    // CRITICAL: Invalidate notifications count query to refresh the badge immediately
    if (effectiveTenant && client?._id) {
      queryClient.invalidateQueries({
        queryKey: ['header', 'notificationsCount', effectiveTenant, client._id]
      });
    }
    
    // CRITICAL: Refetch notifications list after a short delay to ensure server state is synced
    // This ensures any notifications that arrived via socket are also in the server response
    // Use a debounced refetch to avoid rapid-fire requests
    if (effectiveTenant) {
      // Debounce: only refetch once per 1 second even if multiple notifications arrive
      setTimeout(() => {
        fetchNotifications();
      }, 1000);
    }
    
    // Show browser notification if permission granted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new window.Notification('התראה חדשה', {
          body: payload.text,
          icon: '/favicon.ico',
        });
      } catch (err) {
        // Silently handle notification errors
      }
    }
  }, [effectiveTenant, client?._id, queryClient, fetchNotifications]);

  // CRITICAL FIX: Handle notification-deleted socket event
  const handleNotificationDeleted = useCallback((payload: any) => {
    // Normalize: payload might be just an id string, or an object with id/_id
    const notificationId = typeof payload === 'string' 
      ? payload 
      : (payload.id || payload._id || '');
    
    if (!notificationId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Header] Received notification-deleted without id:', payload);
      }
      return;
    }
    
    // Remove notification from local state
    // Defer to prevent [Violation] handler warnings
    Promise.resolve().then(() => {
      setNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notificationId);
        return filtered;
      });
    });
    
    // CRITICAL: Invalidate count query when notification is deleted
    if (effectiveTenant && client?._id) {
      queryClient.invalidateQueries({
        queryKey: ['header', 'notificationsCount', effectiveTenant, client._id]
      });
    }
  }, [effectiveTenant, client?._id, queryClient]);

  // CRITICAL FIX: Handle notification-updated socket event
  // This fires when a notification is updated (e.g., marked as read) on another tab/device
  const handleNotificationUpdated = useCallback((payload: any) => {
    // Normalize payload (backend may send _id or id)
    const notificationId = payload.id || payload._id || '';
    if (!notificationId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Header] Received notification-updated without id:', payload);
      }
      return;
    }
    
    // Normalize to Notification interface
    const normalized: Notification = {
      id: notificationId,
      text: payload.text || '',
      createdAt: payload.createdAt || new Date().toISOString(),
      read: payload.read || false,
      type: payload.type,
    };
    
    // Update notification in local state
    // Defer to prevent [Violation] handler warnings
    Promise.resolve().then(() => {
      setNotifications(prev => {
        const exists = prev.some(x => x.id === normalized.id);
        if (exists) {
          // Update existing notification
          const updated = prev.map(x => (x.id === normalized.id ? normalized : x));
          
          return updated;
        } else {
          // Add new notification if it doesn't exist (might have been created on another tab)
          // Optimize: Only sort if list is small
          const updated = prev.length < 50
            ? [normalized, ...prev].sort((a, b) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              )
            : [normalized, ...prev];
          return updated;
        }
      });
    });
    
    // CRITICAL: Invalidate count query when notification is updated (e.g., marked as read)
    if (effectiveTenant && client?._id) {
      queryClient.invalidateQueries({
        queryKey: ['header', 'notificationsCount', effectiveTenant, client._id]
      });
    }
    
    // CRITICAL: If notification was marked as read, refetch list to ensure consistency
    if (normalized.read) {
      // Small delay to avoid rapid refetches
      setTimeout(() => {
        fetchNotifications();
      }, 500);
    }
  }, [effectiveTenant, client?._id, queryClient, fetchNotifications]);

  // Subscribe to notification events
  useSocketSubscription('notification', handleNotification);
  useSocketSubscription('notification-deleted', handleNotificationDeleted);
  useSocketSubscription('notification-updated', handleNotificationUpdated);

  // Close notifications dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [open]);


  // Use query count if available, fallback to local state
  const unreadCount = unreadNotificationsCount > 0 ? unreadNotificationsCount : notifications.filter(n => !n.read).length;

  return (
    <>
      {/* AppBar */}
      <header
        dir="ltr"
        className="
          fixed top-0 left-0 right-0 z-50
          bg-white/95 backdrop-blur-md
          border-b border-slate-200/60
          shadow-[0_1px_4px_rgba(0,0,0,0.06)]
          px-1.5 sm:px-3 py-1.5 sm:py-2
          h-[48px] sm:h-[56px]
          flex items-center
        "
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
        data-header-height="48"
      >
        <div className="flex items-center justify-between gap-1 sm:gap-3 w-full h-full min-w-0">
          {/* Brand + Menu - Left Side */}
          <div className="flex items-center gap-1 sm:gap-2.5 min-w-0 flex-shrink-0">
            {onToggleSidebar && (
              <button
                onClick={() => { setMenuOpen(!menuOpen); onToggleSidebar(); }}
                className="
                  text-slate-700 hover:text-slate-900 
                  text-lg sm:text-xl sm:hidden 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  active:scale-95 transition-all
                  p-1.5 sm:p-2 rounded-lg 
                  hover:bg-slate-100 active:bg-slate-200
                  touch-manipulation
                  w-8 h-8 sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center flex-shrink-0
                "
                aria-label="פתח תפריט צד"
                aria-expanded={menuOpen}
              >
                <FiMenu />
              </button>
            )}

            <div className="relative flex-shrink-0">
              {/* CRM logo only (WJS Together branding) - never company logo here */}
              {bootstrapLoading ? (
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-slate-200 animate-pulse" />
              ) : (
                <img
                  src="/crmLogo.png"
                  alt="CRM Logo"
                  className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg border border-slate-200/80 shadow-sm bg-white object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )}
              <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-400 ring-2 ring-white shadow-sm" />
            </div>

            {/* Brand name - compact on mobile */}
            <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink-0 overflow-hidden">
              {bootstrapLoading ? (
                <div className="h-4 w-24 bg-slate-200 animate-pulse rounded" />
              ) : (
                <span 
                  className="font-bold text-slate-900 text-[10px] sm:text-base tracking-tight truncate" 
                  style={{ maxWidth: isMobile ? '120px' : 'none' }}
                >
                  <span className="sm:hidden">WJS 365ᵈ Together</span>
                  <span className="hidden sm:inline">WJS 365ᵈ Together</span>
                </span>
              )}
              {companyBadge && (
                <div 
                  className={`hidden sm:inline-flex items-center justify-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-bold text-[8px] sm:text-[11px] uppercase tracking-wider shadow-md ${
                    companyBadge === 'enterprise' 
                      ? 'bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-600 text-white border border-yellow-300/50'
                      : companyBadge === 'platinum' 
                      ? 'bg-black text-white border border-white/20' 
                      : companyBadge === 'vip'
                      ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-white border border-yellow-300/50'
                      : companyBadge === 'basic'
                      ? 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-white border border-amber-400/50'
                      : companyBadge === 'trial'
                      ? 'bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 text-white border border-gray-400/50'
                      : 'bg-gray-400 text-white'
                  }`}
                  style={{
                    boxShadow: companyBadge === 'enterprise'
                      ? '0 4px 12px rgba(255, 215, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.3), 0 0 20px rgba(255, 215, 0, 0.4)'
                      : companyBadge === 'platinum' 
                      ? '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                      : companyBadge === 'vip'
                      ? '0 2px 4px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
                      : companyBadge === 'basic'
                      ? '0 2px 4px rgba(180, 83, 9, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      : companyBadge === 'trial'
                      ? '0 2px 4px rgba(107, 114, 128, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      : '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {companyBadge === 'enterprise' && (
                    <>
                      <span className="hidden xs:inline text-white font-bold">∞</span>
                      <span className="text-white font-bold">ENTERPRISE</span>
                    </>
                  )}
                  {companyBadge === 'platinum' && <span className="text-white font-bold">PLATINUM</span>}
                  {companyBadge === 'vip' && <span className="text-white font-bold">VIP</span>}
                  {companyBadge === 'basic' && <span className="text-white font-bold">BASIC</span>}
                  {companyBadge === 'trial' && <span className="text-white font-bold">TRIAL</span>}
                </div>
              )}
            </div>
          </div>

          {/* Global Search - Center */}
          {client && effectiveTenant && (
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <GlobalSearch company={effectiveTenant} />
            </div>
          )}

          {/* Right cluster - All three cards - Far Right */}
          {client && (
            <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0 ml-auto min-w-0" ref={userMenuRef}>
              {/* Admin company card - Left Card (Desktop only) */}
              {(bootstrapData?.company?.name || adminCompanyName) && (
                <div
                  className="
                    hidden md:flex items-center gap-2 px-2.5 py-1.5
                    rounded-lg bg-slate-50/70 border border-slate-200/60
                    text-slate-700 font-medium text-xs
                    max-w-[180px] lg:max-w-[240px] xl:max-w-[300px] min-w-0
                    hover:bg-slate-100/80 transition-all duration-150
                    shadow-sm hover:shadow
                  "
                  title={bootstrapData?.company?.name || adminCompanyName}
                >
                  {/* logo (if exists) → else briefcase; tiny skeleton while loading */}
                  {/* 
                    Logo loading strategy:
                    - When logo exists → load from bootstrap or adminLogoUrl
                    - When logo does not exist or fails → use briefcase icon placeholder
                    - onError handler ensures fallback to briefcase icon if image fails to load
                  */}
                  {bootstrapLoading || logoLoading ? (
                    <span className="h-6 w-6 rounded-md bg-slate-200 animate-pulse inline-block flex-shrink-0" />
                  ) : (bootstrapData?.company?.logoUrl || adminLogoUrl) ? (
                    <img
                      src={(bootstrapData?.company?.logoUrl || adminLogoUrl) === '/crmLogo.png'
                        ? '/crmLogo.png'
                        : cldThumb(bootstrapData?.company?.logoUrl || adminLogoUrl || '', 24, 24)}
                      alt={bootstrapData?.company?.name || adminCompanyName || 'Company logo'}
                      loading="lazy"
                      decoding="async"
                      className="h-6 w-6 rounded-md object-contain flex-shrink-0 border border-slate-200/50 shadow-sm bg-white"
                      onError={() => {
                        // Only set error once to prevent infinite loops
                        if (!adminLogoError) {
                          setAdminLogoError(true);
                          // Fallback to default logo on error
                          setAdminLogoUrl('/crmLogo.png');
                        }
                      }}
                    />
                  ) : (
                    <img
                      src="/crmLogo.png"
                      alt={bootstrapData?.company?.name || adminCompanyName || 'Company logo'}
                      className="h-6 w-6 rounded-md object-contain flex-shrink-0 border border-slate-200/50 shadow-sm bg-white"
                      onError={() => {
                        // Final fallback to briefcase icon if default logo also fails
                        if (!adminLogoError) {
                          setAdminLogoError(true);
                        }
                      }}
                    />
                  )}

                  <span className="truncate text-slate-700 text-xs font-medium">
                    {bootstrapData?.company?.name || adminCompanyName}
                  </span>
                </div>
              )}

              {/* Utility buttons: Chat, Sticky Notes, Calculator (Desktop) */}
              {(showChat || showStickyNotes || showCalculator) && (
                <div className="hidden md:flex items-center gap-1.5 pl-2 ml-1.5 border-l border-slate-200/40">
                  {showChat && (
                    <button
                      onClick={handleChatClick}
                      className="
                        h-8 w-8 rounded-lg
                        bg-slate-50/90 hover:bg-blue-50
                        border border-slate-200/60 hover:border-blue-300/60
                        text-blue-600 hover:text-blue-700
                        transition-all duration-200
                        active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                        flex items-center justify-center
                        group shadow-sm hover:shadow
                      "
                      aria-label="צ'אט"
                      title="צ'אט"
                    >
                      <FiMessageCircle className="text-sm group-hover:scale-110 transition-transform" />
                    </button>
                  )}

                  {showStickyNotes && (
                    <button
                      onClick={handleStickyNotesClick}
                      className="
                        h-8 w-8 rounded-lg
                        bg-slate-50/90 hover:bg-amber-50
                        border border-slate-200/60 hover:border-amber-300/60
                        text-amber-600 hover:text-amber-700
                        transition-all duration-200
                        active:scale-95 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1
                        flex items-center justify-center
                        group shadow-sm hover:shadow
                      "
                      aria-label="פתקים"
                      title="פתקים"
                    >
                      {/* Custom Pencil Icon - Orange-brown */}
                      <svg 
                        className="text-amber-600 text-sm group-hover:scale-110 transition-transform flex-shrink-0" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  )}

                  {showCalculator && (
                    <button
                      onClick={handleCalculatorClick}
                      className="
                        h-8 w-8 rounded-lg
                        bg-slate-50/90 hover:bg-purple-50
                        border border-slate-200/60 hover:border-purple-300/60
                        text-purple-600 hover:text-purple-700
                        transition-all duration-200
                        active:scale-95 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
                        flex items-center justify-center
                        group shadow-sm hover:shadow
                      "
                      aria-label="מחשבון"
                      title="מחשבון"
                    >
                      <CiCalculator2 className="text-base group-hover:scale-110 transition-transform" />
                    </button>
                  )}
                </div>
              )}

              {/* Mobile: User button - ALWAYS VISIBLE */}
              <div className="relative sm:hidden flex-shrink-0">
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setUserMenuOpen(!userMenuOpen); 
                  }}
                  className="
                    flex items-center gap-1 px-1.5 py-1 rounded-lg
                    bg-gradient-to-r from-orange-50 to-orange-100/80
                    border border-orange-200/60
                    hover:bg-orange-100 active:bg-orange-200 transition-colors
                    touch-manipulation shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
                    flex-shrink-0
                  "
                  style={{ 
                    minWidth: isWideMobile ? '85px' : '65px',
                    maxWidth: isWideMobile ? '100px' : '75px'
                  }}
                  aria-label={`תפריט משתמש - ${displayName}`}
                  aria-expanded={userMenuOpen}
                  title={displayName}
                >
                  <div className="h-5 w-5 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 text-white grid place-items-center font-bold text-[9px] shadow-md flex-shrink-0 ring-1 ring-orange-300/50">
                    {bootstrapLoading ? (
                      <div className="h-full w-full rounded-full bg-orange-300 animate-pulse" />
                    ) : (
                      userInitials || <FiUser className="text-[9px]" />
                    )}
                  </div>
                  {isWideMobile && (bootstrapLoading ? (
                    <div className="h-3 w-12 bg-slate-200 animate-pulse rounded" />
                  ) : displayName && displayName !== 'משתמש' && (
                    <span className="text-slate-900 text-[9px] font-bold truncate max-w-[50px]" title={displayName}>
                      {displayName.split(' ')[0].substring(0, 5)}
                    </span>
                  ))}
                  <FiChevronRight className={`text-slate-400 text-[9px] flex-shrink-0 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mobile user menu dropdown */}
                <AnimatePresence>
                  {userMenuOpen && (
                    <>
                      {/* Backdrop for mobile menu */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 bg-black/30 z-[115] sm:hidden"
                        onClick={() => setUserMenuOpen(false)}
                        aria-hidden="true"
                      />

                      {/* Mobile menu dropdown */}
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="
                          fixed z-[120]
                          right-2 left-2
                          mt-2
                          w-auto bg-white rounded-2xl border-2 border-slate-200/90
                          shadow-2xl max-w-sm mx-auto
                        "
                        style={{ 
                          top: 'calc(48px + 8px)',
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="py-2">
                          {/* User info header on mobile */}
                          <div className="px-4 py-3 border-b border-slate-200/60 mb-2">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 text-white grid place-items-center font-bold text-sm shadow-md flex-shrink-0">
                                {userInitials || <FiUser className="text-sm" />}
                              </div>
                              <div className="flex-1 min-w-0 text-right">
                                <div className="text-slate-900 text-sm font-semibold truncate" title={displayName}>
                                  {displayName}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                  {client?.role === 'admin' && 'אדמין'}
                                  {client?.role === 'manager' && (isFullAccessManager ? 'מנהל בהרשאות אדמן' : 'משתמש')}
                                  {client?.role === 'client' && 'לקוח'}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Hide "Switch Company" for superadmin */}
                          {client?.role !== 'superadmin' && (
                            <>
                              <button
                                onClick={() => {
                                  setSwitchAccountOpen(true);
                                  setUserMenuOpen(false);
                                }}
                                className="
                                  w-full px-4 py-3 text-right text-slate-700 hover:bg-slate-50
                                  active:bg-slate-100 flex items-center gap-3 transition-all duration-150 text-sm
                                  touch-manipulation rounded-lg mx-1
                                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                                "
                              >
                                <FiRefreshCw className="text-slate-500 text-lg flex-shrink-0" />
                                <span className="flex-1">{t('nav.switchCompany')}</span>
                              </button>
                              
                              <div className="border-t border-slate-100 my-1" />
                            </>
                          )}
                          
                          <button
                            onClick={handleSettings}
                            className="
                              w-full px-4 py-3 text-right text-slate-700 hover:bg-slate-50
                              active:bg-slate-100 flex items-center gap-3 transition-all duration-150 text-sm
                              touch-manipulation rounded-lg mx-1
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                            "
                          >
                            <FiSettings className="text-slate-500 text-lg flex-shrink-0" />
                            <span className="flex-1">{t('nav.settings')}</span>
                          </button>
                          
                          <div className="border-t border-slate-100 my-1" />
                          
                          <button
                            onClick={handleLogout}
                            className="
                              w-full px-4 py-3 text-right text-red-600 hover:bg-red-50
                              active:bg-red-100 flex items-center gap-3 transition-all duration-150 
                              font-semibold text-sm touch-manipulation rounded-lg mx-1
                              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset
                            "
                          >
                            <FiLogOut className="text-red-500 text-lg flex-shrink-0" />
                            <span className="flex-1">{t('auth.logoutButton')}</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile utility buttons (show on small screens) - AFTER user button */}
              {/* {showChat && (
                <div className="sm:hidden flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={handleChatClick}
                    className="
                      h-7 w-7 rounded-lg
                      bg-slate-50/90 hover:bg-blue-50
                      border border-slate-200/60 hover:border-blue-300/60
                      text-blue-600 hover:text-blue-700
                      transition-all duration-200
                      active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                      flex items-center justify-center
                      touch-manipulation shadow-sm flex-shrink-0
                    "
                    aria-label="צ'אט"
                    title="צ'אט"
                  >
                    <FiMessageCircle className="text-xs" />
                  </button>
                </div>
              )} */}

              {/* Mobile: 3-dot menu for Calculator and Sticky Notes (show on small screens) */}
              {((showCalculator || showStickyNotes) && (
                <div className="relative sm:hidden flex-shrink-0" ref={moreMenuRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMoreMenuOpen(!moreMenuOpen);
                    }}
                    className="
                      h-7 w-7 rounded-lg
                      bg-slate-50/90 hover:bg-slate-100
                      border border-slate-200/60
                      text-slate-600 hover:text-slate-700
                      transition-all duration-200
                      active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                      flex items-center justify-center
                      touch-manipulation shadow-sm flex-shrink-0
                    "
                    aria-label="עוד אפשרויות"
                    title="עוד אפשרויות"
                    aria-expanded={moreMenuOpen}
                  >
                    <FiMoreVertical className="text-sm" />
                  </button>

                  {/* Mobile more menu dropdown */}
                  <AnimatePresence>
                    {moreMenuOpen && (
                      <>
                        {/* Backdrop for mobile menu */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="fixed inset-0 bg-black/30 z-[115] sm:hidden"
                          onClick={() => setMoreMenuOpen(false)}
                          aria-hidden="true"
                        />

                        {/* Mobile menu dropdown */}
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                          className="
                            fixed z-[120]
                            right-2 left-2
                            mt-2
                            w-auto bg-white rounded-2xl border-2 border-slate-200/90
                            shadow-2xl max-w-sm mx-auto
                          "
                          style={{ 
                            top: 'calc(48px + 8px)',
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="py-2">
                            {showCalculator && (
                              <button
                                onClick={() => {
                                  handleCalculatorClick();
                                  setMoreMenuOpen(false);
                                }}
                                className="
                                  w-full px-4 py-3 text-right text-slate-700 hover:bg-slate-50
                                  active:bg-slate-100 flex items-center gap-3 transition-all duration-150 text-sm
                                  touch-manipulation rounded-lg mx-1
                                  focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset
                                "
                              >
                                <CiCalculator2 className="text-purple-600 text-lg flex-shrink-0" />
                                <span className="flex-1">מחשבון</span>
                              </button>
                            )}
                            
                            {showStickyNotes && (
                              <>
                                {showCalculator && <div className="border-t border-slate-100 my-1" />}
                                <button
                                  onClick={() => {
                                    handleStickyNotesClick();
                                    setMoreMenuOpen(false);
                                  }}
                                  className="
                                    w-full px-4 py-3 text-right text-slate-700 hover:bg-slate-50
                                    active:bg-slate-100 flex items-center gap-3 transition-all duration-150 text-sm
                                    touch-manipulation rounded-lg mx-1
                                    focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-inset
                                  "
                                >
                                  {/* Custom Pencil Icon - Orange-brown */}
                                  <svg 
                                    className="text-amber-600 text-lg flex-shrink-0" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  <span className="flex-1">ניהול פתקים</span>
                                </button>
                              </>
                            )}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* Mobile: Search icon button */}
              {client && effectiveTenant && (
                <div className="relative sm:hidden flex-shrink-0" ref={searchRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchOpen(!searchOpen);
                    }}
                    className="
                      h-7 w-7 rounded-lg
                      bg-slate-50/90 hover:bg-slate-100
                      border border-slate-200/60
                      text-slate-600 hover:text-slate-700
                      transition-all duration-200
                      active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                      flex items-center justify-center
                      touch-manipulation shadow-sm flex-shrink-0
                    "
                    aria-label="חיפוש"
                    title="חיפוש"
                    aria-expanded={searchOpen}
                  >
                    <FiSearch className="text-sm" />
                  </button>

                  {/* Mobile search popup */}
                  <AnimatePresence>
                    {searchOpen && (
                      <>
                        {/* Backdrop */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="fixed inset-0 bg-black/30 z-[115] sm:hidden"
                          onClick={() => setSearchOpen(false)}
                          aria-hidden="true"
                        />

                        {/* Search popup */}
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                          className="
                            fixed z-[120]
                            right-2 left-2
                            mt-2
                            w-auto bg-white rounded-2xl border-2 border-slate-200/90
                            shadow-2xl max-w-sm mx-auto
                          "
                          style={{ 
                            top: 'calc(48px + 8px)',
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="p-4">
                            <GlobalSearch company={effectiveTenant} />
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Notifications */}
              <div className="relative flex-shrink-0" ref={notifRef}>
                <button
                  onClick={e => { e.stopPropagation(); handleOpenNotifications(); }}
                  className="
                    relative h-7 w-7 sm:h-9 sm:w-9 rounded-lg
                    bg-slate-50/90 hover:bg-slate-100 border border-slate-200/60
                    flex items-center justify-center
                    transition-all duration-200 
                    active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                    shadow-sm hover:shadow touch-manipulation flex-shrink-0
                  "
                  aria-label="התראות"
                  title={`התראות${unreadCount > 0 ? ` (${unreadCount} חדשות)` : ''}`}
                  aria-expanded={open}
                >
                  <FiBell className={`text-xs sm:text-sm ${unreadCount > 0 ? 'text-blue-600' : 'text-slate-600'}`} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[9px] shadow-md ring-2 ring-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications panel (animated) */}
                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 8, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="
                        fixed z-[110]
                        w-[380px] max-w-[92vw]
                        bg-white rounded-xl border border-slate-200/80
                        shadow-2xl
                        flex flex-col
                        overflow-hidden
                      "
                      onClick={e => e.stopPropagation()}
                      style={{ 
                        top: 'calc(48px + 8px)',
                        right: typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
                          ? (sidebarWidth === '16rem' ? '16rem' : '4.5rem')
                          : '0.5rem',
                        marginTop: '4px', 
                        maxHeight: 'calc(100vh - 80px)',
                        height: 'auto'
                      }}
                    >
                      <div className="sticky top-0 z-10 p-4 bg-white border-b border-slate-200/60 rounded-t-xl shadow-sm" dir="rtl">
                        {/* Header row with title and count */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-slate-800 text-base">התראות</h3>
                            {unreadCount > 0 && (
                              <span className="inline-flex flex-col items-center justify-center bg-blue-50 text-blue-700 rounded-lg px-3 py-1.5 min-w-[60px]">
                                <span className="text-base font-bold leading-none">{unreadCount}</span>
                                <span className="text-[10px] font-medium leading-tight mt-0.5">חדשות</span>
                              </span>
                            )}
                          </div>
                          <button
                            className="text-slate-400 hover:text-slate-600 transition p-1.5 rounded-md hover:bg-slate-100 touch-manipulation flex-shrink-0"
                            onClick={() => setOpen(false)}
                            aria-label="סגור התראות"
                          >
                            <FiX className="text-lg" />
                          </button>
                        </div>

                        {/* Action buttons row */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            {/* Notification permission button - only show if permission is default */}
                            {typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default' && (
                              <button
                                onClick={requestNotificationPermission}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors touch-manipulation whitespace-nowrap"
                                title="אפשר התראות דפדפן"
                              >
                                אפשר התראות
                              </button>
                            )}
                            {unreadCount > 0 && (
                              <button
                                onClick={markAllRead}
                                className="text-xs text-slate-700 hover:text-slate-900 font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors touch-manipulation whitespace-nowrap"
                                title="סמן הכל כנקרא"
                              >
                                סמן הכל
                              </button>
                            )}
                          </div>
                          <button
                            onClick={clearAllNotifications}
                            disabled={busyClear || notifications.length === 0}
                            className="text-xs text-rose-600 hover:text-rose-700 font-medium px-3 py-1.5 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex items-center gap-1.5 whitespace-nowrap"
                            title="נקה הכל"
                          >
                            <FiX className="text-xs" />
                            <span>נקה</span>
                          </button>
                        </div>
                      </div>

                      {/* Scrollable notifications list */}
                      <div className="flex-1 overflow-y-auto min-h-0" dir="rtl" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
                        {loading ? (
                          <div className="p-4">
                            {[1, 2, 3].map(k => (
                              <div key={k} className="flex items-start gap-3 py-3 animate-pulse border-b border-slate-100/60">
                                <div className="h-2.5 w-2.5 mt-1 rounded-full bg-slate-200" />
                                <div className="flex-1">
                                  <div className="h-4 w-3/4 rounded bg-slate-200" />
                                  <div className="h-3 w-1/3 rounded bg-slate-100 mt-2" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : notifications.length === 0 ? (
                          <div className="p-8 text-slate-400 text-sm text-center">אין התראות כרגע.</div>
                        ) : (
                          notifications.map((n) => (
                            <div
                              key={n.id}
                              role="button"
                              tabIndex={0}
                              className={`
                                w-full p-3 flex items-start justify-between gap-3 group cursor-pointer
                                ${!n.read ? 'bg-blue-50 text-slate-900 border-r-3 border-blue-500' : 'text-slate-700 bg-white'}
                                hover:bg-slate-50/80 transition-colors border-b border-slate-100/60
                                text-right touch-manipulation
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                              `}
                              onClick={() => { 
                                markAsRead(n.id); 
                                setOpen(false);
                                // If it's a task_note notification, navigate to the task
                                if (n.type === 'task_note' && n.refId && effectiveTenant && client?.role === 'admin') {
                                  // Navigate to admin tasks page and open the task
                                  navigate(`/${effectiveTenant}/admin/tasks?taskId=${n.refId}`);
                                } else {
                                  // Show modal for other notification types
                                  setModalNotif(n);
                                }
                              }}
                              onKeyDown={(e) => { 
                                if (e.key === 'Enter' || e.key === ' ') { 
                                  markAsRead(n.id); 
                                  setOpen(false);
                                  // If it's a task_note notification, navigate to the task
                                  if (n.type === 'task_note' && n.refId && effectiveTenant && client?.role === 'admin') {
                                    navigate(`/${effectiveTenant}/admin/tasks?taskId=${n.refId}`);
                                  } else {
                                    setModalNotif(n);
                                  }
                                } 
                              }}
                              title="הצג התראה"
                            >
                              <div className="text-right flex-1 min-w-0">
                                <div className="text-sm leading-relaxed break-words">{n.text}</div>
                                <div className="text-xs text-slate-400 ltr:text-left rtl:text-right mt-1.5">
                                  {new Date(n.createdAt).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}
                                </div>
                              </div>
                              <button
                                className="ms-2 text-slate-400 hover:text-rose-500 opacity-70 hover:opacity-100 transition p-1.5 rounded-md hover:bg-rose-50 flex-shrink-0 touch-manipulation"
                                title="מחק התראה"
                                onClick={async (e) => { 
                                  e.stopPropagation(); 
                                  await deleteNotificationHandler(n.id); 
                                }}
                                aria-label="מחק"
                              >
                                <FiX className="text-sm" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {/* Footer with total count (optional) */}
                      {notifications.length > 0 && !loading && (
                        <div className="px-4 py-2.5 bg-slate-50/80 border-t border-slate-200/60 text-xs text-slate-500 text-center flex-shrink-0" dir="rtl">
                          סה"כ {notifications.length} התראות
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Desktop user button */}
              <div className="relative hidden sm:block">
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setUserMenuOpen(!userMenuOpen); 
                  }}
                  className="
                    flex items-center gap-2 px-2 py-1.5
                    rounded-lg bg-slate-50/70 border border-slate-200/60
                    hover:bg-slate-100/80 transition-all duration-150 cursor-pointer
                    min-w-0 shadow-sm hover:shadow
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                  "
                  aria-label="תפריט משתמש"
                  aria-expanded={userMenuOpen}
                >
                  <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 text-white grid place-items-center font-bold text-xs flex-shrink-0 shadow-md ring-2 ring-white/50">
                    {bootstrapLoading ? (
                      <div className="h-full w-full rounded-full bg-orange-300 animate-pulse" />
                    ) : (
                      userInitials || <FiUser className="text-xs" />
                    )}
                  </div>
                  <div className="leading-tight text-right flex-1 min-w-0 hidden lg:block">
                    {bootstrapLoading ? (
                      <>
                        <div className="h-3 w-20 bg-slate-200 animate-pulse rounded mb-1" />
                        <div className="h-2 w-16 bg-slate-100 animate-pulse rounded" />
                      </>
                    ) : (
                      <>
                        <div className="text-slate-900 text-xs font-semibold truncate max-w-[120px]" title={displayName}>
                          {displayName}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[120px]">
                          {client?.role === 'admin' && 'אדמין'}
                          {client?.role === 'manager' && (isFullAccessManager ? 'מנהל בהרשאות אדמן' : 'משתמש')}
                          {client?.role === 'client' && 'לקוח'}
                        </div>
                      </>
                    )}
                  </div>
                  <FiChevronRight className="text-slate-400 text-xs flex-shrink-0 hidden xl:block" />
                </button>

                {/* Desktop user menu dropdown */}
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 8, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="
                        absolute z-[120] right-0 mt-2
                        w-52 bg-white rounded-xl border border-slate-200/80
                        shadow-2xl
                      "
                      style={{ 
                        top: '100%'
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="py-2">
                        {/* Hide "Switch Company" for superadmin */}
                        {client?.role !== 'superadmin' && (
                          <>
                            <button
                              onClick={() => {
                                setSwitchAccountOpen(true);
                                setUserMenuOpen(false);
                              }}
                              className="
                                w-full px-3 py-2.5 text-right text-slate-700 hover:bg-slate-50
                                active:bg-slate-100 flex items-center gap-2 transition-all duration-150 text-sm
                                touch-manipulation rounded-md
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                              "
                            >
                              <FiRefreshCw className="text-slate-500 text-base flex-shrink-0" />
                              {t('nav.switchCompany')}
                            </button>
                            
                            <div className="border-t border-slate-100 my-1" />
                          </>
                        )}
                        
                        <button
                          onClick={handleSettings}
                          className="
                            w-full px-3 py-2.5 text-right text-slate-700 hover:bg-slate-50
                            active:bg-slate-100 flex items-center gap-2 transition-all duration-150 text-sm
                            touch-manipulation rounded-md
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                          "
                        >
                          <FiSettings className="text-slate-500 text-base flex-shrink-0" />
                          {t('nav.settings')}
                        </button>
                        
                        <div className="border-t border-slate-100 my-1" />
                        
                        <button
                          onClick={handleLogout}
                          className="
                            w-full px-3 py-2.5 text-right text-red-600 hover:bg-red-50
                            active:bg-red-100 flex items-center gap-2 transition-all duration-150 
                            font-semibold text-sm touch-manipulation rounded-md
                            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset
                          "
                        >
                          <FiLogOut className="text-red-500 text-base flex-shrink-0" />
                          {t('auth.logoutButton')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Notification modal */}
      <AnimatePresence>
        {modalNotif && (
          <motion.div
            className="fixed inset-0 z-[1000] bg-black/40 grid place-items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalNotif(null)}
          >
            <motion.div
              className="
                bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl
                w-full max-w-md max-h-[85vh] flex flex-col relative
                overflow-hidden
              "
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 10, opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header with close button */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-200/60 flex-shrink-0">
                <h2 className="text-slate-900 text-xl font-bold text-center flex-1">התראה</h2>
                <button
                  className="text-slate-400 hover:text-rose-500 text-2xl p-1 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
                  onClick={() => setModalNotif(null)}
                  aria-label="סגור"
                >
                  <FiX />
                </button>
              </div>
              
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
                <div className="text-slate-800 text-base break-words whitespace-pre-line text-right leading-relaxed">
                  {modalNotif.text}
                </div>
              </div>
              
              {/* Footer with timestamp */}
              <div className="px-6 py-4 border-t border-slate-200/60 text-xs text-slate-400 text-left flex-shrink-0">
                {new Date(modalNotif.createdAt).toLocaleString('he-IL')}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toasts container */}
      <ToastContainer position="top-right" newestOnTop rtl theme="colored" limit={1} />

      {/* Switch Account Modal */}
      {/* Hide SwitchAccount modal for superadmin */}
      {client?.role !== 'superadmin' && (
        <SwitchAccount isOpen={switchAccountOpen} onClose={() => setSwitchAccountOpen(false)} />
      )}

      {/* Email Inbox Popup - Only for staff (exclude clients) */}
      {showEmail && (
        <EmailInboxPopup
          isOpen={emailPopupOpen}
          onClose={() => setEmailPopupOpen(false)}
        />
      )}
      
      {/* Logout Confirmation Modal */}
      {confirmLogout && (
        <>
          <style>{`
            @keyframes modal-fade-in {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes modal-scale-in {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(-10px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            @keyframes icon-shake {
              0%, 100% {
                transform: rotate(0deg);
              }
              25% {
                transform: rotate(-5deg);
              }
              75% {
                transform: rotate(5deg);
              }
            }
            @keyframes pulse-glow {
              0%, 100% {
                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
              }
              50% {
                    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
              }
            }
            .logout-modal-backdrop {
              animation: modal-fade-in 0.2s ease-out;
            }
            .logout-modal-content {
              animation: modal-scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            .logout-icon-animation {
              animation: icon-shake 2s ease-in-out infinite;
            }
            .logout-button-red {
              animation: pulse-glow 2s ease-in-out infinite;
            }
          `}</style>
          <div className="fixed inset-0 z-[120] logout-modal-backdrop">
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-sm" onClick={() => setConfirmLogout(false)}></div>
            <div className="relative flex items-center justify-center min-h-screen p-4">
              <div className="logout-modal-content bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-red-50 via-orange-50 to-red-50 p-6 border-b border-red-100">
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center logout-icon-animation">
                        <FiLogOut className="w-8 h-8 text-red-600" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 text-center">
                    {t('auth.logoutModalTitle')}
                  </h3>
                </div>
                
                {/* Content */}
                <div className="p-8 text-center">
                  <p className="text-gray-700 text-lg mb-2 font-medium">
                    {t('auth.logoutConfirmMessage')}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {t('auth.logoutRedirectMessage')}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="p-6 bg-gray-50 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => setConfirmLogout(false)}
                    className="flex-1 px-6 py-3 rounded-xl bg-white border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 active:scale-95 transition-all duration-200 shadow-sm"
                  >
                    {t('auth.cancel')}
                  </button>
                  <button
                    onClick={performLogout}
                    className="flex-1 px-6 py-3 rounded-xl logout-button-red bg-gradient-to-r from-red-600 to-red-700 text-white font-bold hover:from-red-700 hover:to-red-800 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {t('auth.logoutButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Logout Overlay - shows during manual logout */}
      <LogoutOverlay visible={isLoggingOut} />
    </>
  );
};

export default Header;
